import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/** Messages exchanged between open tabs of this app. */
export interface TabSyncMessage {
    type: 'token' | 'activity';
    /** New access token — 'token' messages only. */
    token?: string;
    /** New refresh token — 'token' messages only. */
    refreshToken?: string;
}

/**
 * One BroadcastChannel shared by every tab of this origin, used to keep tabs of the SAME
 * login in step.
 *
 * Why this exists: auth lives in sessionStorage, which is per-tab — but a DUPLICATED tab
 * (duplicate/middle-click/target=_blank) inherits a COPY of it. Each tab then refreshed its
 * own copy on its own timer, so the slower tab presented an already-rotated refresh token
 * and the API treated it as token theft, logging the user out everywhere. Tabs therefore
 * need to tell each other (a) when a token has been rotated, and (b) that the user is
 * active, so a background tab does not idle-out while the user works in another tab.
 *
 * BroadcastChannel never echoes to the sender, so a receiver must not re-broadcast what it
 * receives. Unsupported browsers degrade to no cross-tab sync (each tab behaves as before).
 */
@Injectable({ providedIn: 'root' })
export class TabSyncService implements OnDestroy {

    private static readonly CHANNEL = 'labour-app-tab-sync';

    private channel: BroadcastChannel | null = null;
    private readonly incoming = new Subject<TabSyncMessage>();

    constructor() {
        if (typeof BroadcastChannel === 'undefined') {
            return; // Older browser — no cross-tab sync; per-tab behaviour is unchanged.
        }
        try {
            this.channel = new BroadcastChannel(TabSyncService.CHANNEL);
            this.channel.onmessage = (event: MessageEvent) => {
                const message = event?.data as TabSyncMessage;
                if (message?.type) {
                    this.incoming.next(message);
                }
            };
        } catch {
            this.channel = null;
        }
    }

    /** Messages from OTHER tabs of the given type. Never fires for this tab's own posts. */
    on(type: TabSyncMessage['type']): Observable<TabSyncMessage> {
        return this.incoming.asObservable().pipe(filter((m) => m.type === type));
    }

    /** Best-effort broadcast to the other tabs; a failure here must never break the caller. */
    post(message: TabSyncMessage): void {
        try {
            this.channel?.postMessage(message);
        } catch {
            // Channel closed or message not cloneable — cross-tab sync is an optimisation.
        }
    }

    ngOnDestroy(): void {
        try {
            this.channel?.close();
        } catch {
            // Already closed.
        }
        this.channel = null;
    }
}
